<template>
  <div class="kpi-score-by-tags">
    <HorizontalLoading v-if="KpiScoreByTags.isLoading" />
    <div class="chart-header">
      <div class="title">
        <div class="top-title-wrapper" :class="{'expanded-graph': isBtnExpand}">
          <span> {{ $t('{kpiName}', { kpiName: normalizeString(selectedKpi), title: filterTitle }) }} </span>
          <div class="subTitle">
            {{ $t('Category Score Averages') }}
          </div>
        </div>
        <el-breadcrumb separator="/">
          <el-breadcrumb-item>
            <el-button type="text"
                       :disabled="visitedTags.length === 0"
                       @click="backToRoot">
              {{ tagType.name }}
            </el-button>
          </el-breadcrumb-item>
          <el-breadcrumb-item v-for="(tag, index) in visitedTags" :key="tag.id">
            <el-button type="text"
                       :disabled="index === visitedTags.length - 1"
                       @click="backToPreviousTag(tag,index)">
              {{ tag.name }}
            </el-button>
          </el-breadcrumb-item>
        </el-breadcrumb>
      </div>
      <ChartButtons class="kpi-score-by-tags-chart-buttons"
                    :is-btn-expand="isBtnExpand"
                    :is-expand-btn-visible="true"
                    :only-charts-btn="false"
                    @expandChartToggle="expandChartToggle"
                    @chartDownload="chartDownload" />
    </div>

    <div class="chart" />
    <ContinueToOtherTabs ref="continueToOtherTabsKpiScoreByTags"
                         :project-id="projectId" />
  </div>
</template>

<script>
import { createCrudState } from 'vuex-module-generator';
import Highcharts from 'highcharts';
import {
  chain,
  map,
  forEach,
  result,
  isEqual,
  sortBy
} from 'lodash';
import ChartButtons from '@/components/ChartButtons';
import chartButtonAction from '@/helpers/mixins/chartButtonAction';
import ContinueToOtherTabs from '@/components/ContinueToOtherTabs';
import { fetchKpiViewByTags } from '../api';
import ExecutiveViewKpiBus from '../bus';
import { getTemplateProps, formatDataLabel } from '../../executive-view/utils';
import { fetchTags } from '../../tag-filterer/api';

export default {
  name: 'KpiScoreByTags',
  components: {
    ChartButtons,
    ContinueToOtherTabs
  },
  mixins: [chartButtonAction],
  props: {
    periods: {
      type: Object,
      required: true
    },
    projectId: {
      type: [String, Number],
      required: true
    },
    tagType: {
      type: Object,
      required: true
    },
    selectedKpi: {
      type: String,
      required: true
    },
    filterTitle: {
      type: String,
      required: true
    },
    auditorAndStoreFilters: {
      type: Object,
      required: true
    },
    collectedTags: {
      type: Array,
      default() {
        return [];
      }
    },
    teamGroupFilter: {
      type: Object,
      required: true
    },
    tagIndex: {
      type: [Number, String],
      required: true
    },
    isBtnExpand: {
      type: Boolean,
      default: false
    },
    filterId: {
      type: [Number, String],
      default: null
    },
    sortValue: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      ExecutiveViewKpiBus,
      visitedTags: [],
      KpiScoreByTags: createCrudState().active,
      tags: this.tagType.tags
    };
  },
  computed: {
    filteredCollectedTagsIds() {
      return this.collectedTags.reduce((acc, item) => {
        if (item.root.id !== this.tagType.id) {
          acc.push(...item.tags.map(tag => tag.id));
        }

        return acc;
      }, []);
    },
    templateProps() {
      return getTemplateProps(this.selectedKpi);
    },
    currentTransposedList() {
      return this.getTransposedList(this.KpiScoreByTags.data.current);
    },
    previousTransposedList() {
      return this.getTransposedList(this.KpiScoreByTags.data.previous);
    },
    series() {
      const checkVisibility = (item) => {
        const foundLegend = ExecutiveViewKpiBus.legends.find(l => l.name === item);

        return foundLegend ? foundLegend.visible : true;
      };

      function sortList(list, sort) {
        if (!sort) return list;

        // sort by name
        if (sort === 'name') return list.sort((a, b) => (b.name.toLowerCase() > a.name.toLowerCase() ? -1 : 1));

        // sort by category
        return list.sort((a, b) => (b.y - a.y));
      }

      let list = chain(this.currentTransposedList)
        .map((value, categoryName) => {
          let data = map(value, (score, tagName) => ({
            name: tagName,
            y: score,
            previous: result(this.previousTransposedList, [categoryName, tagName], null)
          }));

          // sorting items
          if (this.sortValue) data = sortList(data, this.sortValue);

          // serie placement by order
          data = data.map((item, index) => ({
            ...item,
            x: index
          }));

          return {
            name: categoryName,
            visible: checkVisibility(categoryName),
            data
          };
        })
        .value();

      // filter by category
      if (this.sortValue && this.sortValue !== 'name') {
        list = list.filter(i => i.name === this.sortValue);
      }

      return sortBy(list, 'name');
    }
  },
  watch: {
    selectedKpi() {
      this.renderChart();
    },
    periods() {
      this.fetchData();
    },
    auditorAndStoreFilters() {
      this.fetchData();
    },
    filteredCollectedTagsIds(oldVal, newVal) {
      if (!isEqual(oldVal, newVal)) {
        this.fetchData();
      }
    },
    teamGroupFilter() {
      this.fetchData();
    },
    sortValue() {
      this.renderChart();
    }
  },
  mounted() {
    this.fetchData();

    ExecutiveViewKpiBus.$on('toggleLegend', () => {
      this.renderChart();
    });
    ExecutiveViewKpiBus.$on('DELETE_TAG', (tag) => {
      this.deleteFunc(tag);
    });
  },
  methods: {
    renderChart() {
      const dataLabelsEnabled = ExecutiveViewKpiBus.legends.filter(legend => legend.visible).length === 1;

      const { templateProps, fetchChildrenTags } = this;

      const getOptions = () => ({
        exporting: {
          buttons: {
            contextButton: {
              enabled: false,
              menuItems: ['downloadPNG', 'downloadJPEG', 'downloadCSV', 'downloadSVG']
            }
          }
        },
        chart: {
          type: 'column'
        },
        xAxis: {
          type: 'category',
          labels: {
            style: {
              cursor: 'pointer'
            },
            events: {
              click() {
                fetchChildrenTags(this.value);
              }
            }
          }
        },
        plotOptions: {
          column: {
            dataLabels: {
              enabled: dataLabelsEnabled,
              useHTML: true,
              align: 'center',
              formatter() {
                const { point: { y, previous } } = this;

                return formatDataLabel(y, previous, templateProps);
              }
            }
          }
        },
        tooltip: {
          headerFormat: '',
          positioner() {
            return {
              x: this.chart.plotLeft,
              y: 0
            };
          },
          pointFormatter() {
            const { float_digits, symbol } = templateProps;

            return this.tooltipFormatter(`<span style="color:{point.color}">●</span> {series.name}: <b>${this.y.toFixed(float_digits)}${symbol}</b><br/>`);
          }
        },
        yAxis: { title: '' },
        title: { text: '', margin: 0 },
        legend: { enabled: false },
        credits: { enabled: false },
        series: this.series
      });

      if (this.chart) {
        this.chart.update(getOptions(), true, true, true);
      } else {
        this.chart = Highcharts.chart(this.$el.querySelector('.chart'), getOptions());
      }
    },
    async fetchData() {
      const {
        projectId, periods, tags, filteredCollectedTagsIds, auditorAndStoreFilters, teamGroupFilter
      } = this;

      const tag_ids = tags.map(tag => tag.id);

      this.KpiScoreByTags.isLoading = true;

      try {
        const { data: { data: current } } = await fetchKpiViewByTags(projectId, {
          filter: {
            project: projectId,
            stores: {
              tags: filteredCollectedTagsIds
            },
            auditor_ids: auditorAndStoreFilters.auditor_ids,
            store_ids: auditorAndStoreFilters.store_ids,
            team_group_id: teamGroupFilter.id,
            start_date_with_time: this.convertToServerTimeZone(periods.current.start_time_without_utc),
            end_date_with_time: this.convertToServerTimeZone(periods.current.end_time_without_utc)
          },
          tag_ids
        });

        const { data: { data: previous } } = await fetchKpiViewByTags(projectId, {
          filter: {
            project: projectId,
            stores: {
              tags: filteredCollectedTagsIds
            },
            auditor_ids: auditorAndStoreFilters.auditor_ids,
            store_ids: auditorAndStoreFilters.store_ids,
            team_group_id: teamGroupFilter.id,
            ...((periods.previous && periods.previous.start_time) && { start_date_with_time: this.convertToServerTimeZone(periods.previous.start_time, undefined, true) }),
            ...((periods.previous && periods.previous.end_time) && { end_date_with_time: this.convertToServerTimeZone(periods.previous.end_time, undefined, true) })
          },
          tag_ids
        });

        this.KpiScoreByTags.data = {
          current,
          previous
        };

        await this.$nextTick();

        this.renderChart();

        this.KpiScoreByTags.isLoaded = true;
      } catch (e) {
        this.KpiScoreByTags.hasError = true;
      }

      this.KpiScoreByTags.isLoading = false;
    },
    async fetchChildrenTags(tagName) {
      const clickedTag = this.tags.find(tag => tag.name === tagName);
      const [lastVisitedTag = {}] = [...this.visitedTags].reverse();

      if (lastVisitedTag.id === clickedTag.id) {
        localStorage.removeItem('selectedTags');
        localStorage.setItem('selectedTags', JSON.stringify(this.collectedTags));

        this.$refs.continueToOtherTabsKpiScoreByTags.openDialog = true;
      } else {
        const { data: { data } } = await fetchTags(clickedTag.id);
        if (data.length) {
          this.visitedTags.push(clickedTag);

          this.tags = data;
          await this.fetchData();
          ExecutiveViewKpiBus.$emit('TAG_FILTERED');
        } else {
          this.visitedTags.push(clickedTag);
          this.tags = [clickedTag];
          await this.fetchData();
          ExecutiveViewKpiBus.$emit('TAG_FILTERED');
        }
      }
    },
    async backToPreviousTag(tag, index) {
      const rest = this.visitedTags.slice(0, index + 1);
      this.$set(this, 'visitedTags', rest);
      const { data: { data } } = await fetchTags(tag.id);
      this.tags = data;
      await this.fetchData();
      ExecutiveViewKpiBus.$emit('TAG_FILTERED');
    },
    async backToRoot() {
      this.$set(this, 'visitedTags', []);
      this.tags = this.tagType.tags;
      await this.fetchData();
      ExecutiveViewKpiBus.$emit('TAG_FILTERED');
    },
    async deleteFunc(tag) {
      let rest = '';
      if (this.visitedTags.length !== 0) {
        if (this.visitedTags.length === 1) {
          if (this.visitedTags[0].id === tag.id) {
            rest = this.visitedTags.slice(0, 0);
            this.$set(this, 'visitedTags', []);
            this.tags = this.tagType.tags;
            await this.fetchData();
          }
        } else {
          for (let i = 0; i <= this.visitedTags.length; i++) {
            if (this.visitedTags[i].id === tag.id) {
              rest = this.visitedTags.slice(0, i);
              this.$set(this, 'visitedTags', rest);
              // eslint-disable-next-line no-await-in-loop
              const { data: { data } } = await fetchTags(rest[0].id);
              this.tags = data;
              // eslint-disable-next-line no-await-in-loop
              await this.fetchData();
            }
          }
        }
      }

      ExecutiveViewKpiBus.$emit('TAG_FILTERED');
    },
    getTransposedList(list) {
      if (!list) return [];

      return chain(list)
        .find(item => item.key === this.selectedKpi)
        .result('value', [])
        .reduce((acc, tag) => {
          forEach(tag.value, (category) => {
            if (!acc[category.key]) {
              acc[category.key] = {};
            }

            acc[category.key][tag.key] = category.value;
          });

          return acc;
        }, {})
        .value();
    },
    reflowChart() {
      this.chart.reflow();
    }
  }
};
</script>

<style lang="scss">
.kpi-score-by-tags {
  position: relative;
  padding: 20px;
  margin-bottom: 20px;
  background-color: white;

  .chart-header {
    display: flex;
    justify-content: space-between;

    .title {
      margin-bottom: 20px;

      .top-title-wrapper {
        @media (max-width: 767px) {
          display: flex;
          column-gap: 10px;
          align-items: center;
        }

        @media (max-width: 640px) {
          flex-direction: column;
          align-items: flex-start;
        }

        .subTitle {
          font-size: 16px;
          color: #555;

          @media (max-width: 767px) {
            font-size: 18px;
          }

          @include breakpoint(md-s-max) {
            font-size: 22px;
          }
        }

        span {
          font-size: 18px;
          font-weight: 600;
          color: $darkSkyBlue;

          @media (max-width: 767px) {
            font-size: 18px;
          }

          @include breakpoint(md-s-max) {
            font-size: 16px;
          }
        }

        &.expanded-graph {
          display: flex;
          column-gap: 10px;
          align-items: center;

          span,
          .subTitle {
            font-size: 18px;
          }
        }
      }
    }
  }
}

.kpi-score-by-tags-chart-buttons {
  @media (max-width: 767px) {
    .expand-btn {
      display: none !important;
    }
  }
}
</style>
